import React from "react";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { formatPhone } from "./form_utils";
import {
  actual_cash_down_payment,
  apr,
  estimated_cash_to_close_down_payment_with_closing_costs,
  loan_amount_first_mortgage,
  loan_term,
  total_closing_costs,
  total_cost_to_purchase,
  total_monthly_payment
} from "../../../calculations/loan";
import { loan_product } from "../../../constants/utils";
import Image from "../../ui/image";
import "./css/open_house_flyer.scss";

function money(v) {
  const n = parseFloat((v == null ? 0 : v).toString());
  if (!n || isNaN(n)) return "$0";
  return "$" + Math.round(n).toLocaleString("en-US");
}
function pctFixed(v, d) {
  const n = parseFloat((v == null ? 0 : v).toString());
  if (isNaN(n)) return "";
  return n.toFixed(d) + "%";
}
function pctTrim(v, d) {
  return pctFixed(v, d).replace(/\.?0+%$/, "%");
}

// Open House Flyer (Purchase only). Financing options for Loans 1-4 for a listed
// property. Data: property/list price (New Property + Down Payment pages), loan
// figures (Down Payment page calcs), Listing Agent = Seller's Agent (Realtor Agent
// form), Loan Officer + company + logo (Admin Panel). Highlight + heading colors
// match the company logo (green / gold).
export default function OpenHouseFlyer({ homePhoto }) {
  const [{ companyProfile }] = useUser();
  const [document, , { documentType, documentAccountProfile }] = useDocument();
  const {
    property,
    property_to_be_sold,
    is_selling_property,
    realtor_contacts
  } = document;

  const prop = property || {};
  const lo = documentAccountProfile || {};
  const co = companyProfile || {};
  // Listing Agent = the "Listing Agent" block on the Realtor Agent form (stored
  // under the buyer_agent key). The Pre-Qual / Pre-Approval letters use the
  // Buyer's Agent (Selling Agent) block instead (seller_agent key).
  const agent = (realtor_contacts && realtor_contacts.buyer_agent) || {};
  const loans = (document.loans || []).slice(0, 4);

  const propLine1 = [prop.address, prop.address2].filter(Boolean).join(" ");
  const propLine2 =
    [prop.city, prop.state].filter(Boolean).join(", ") +
    (prop.zip ? " " + prop.zip : "");

  const today = new Date();
  const effDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;

  function paymentType(l) {
    const t = loan_term(l).toString();
    return t === "360" ? "360 – Amortized" : `${t} months`;
  }
  const agentPhone = agent.office_phone || agent.cell_phone || "";

  return (
    <div className="ohf-doc">
      <div className="ohf-top">
        <div className="ohf-listing">
          <div className="ohf-addr">
            {propLine1 || "Property Address"}
            {propLine2 && (
              <>
                <br />
                {propLine2}
              </>
            )}
          </div>
          <div className="ohf-row">
            <span className="ohf-lbl">Listing Agent:</span> {agent.name}
          </div>
          <div className="ohf-row">
            <span className="ohf-lbl">Listing Company:</span> {agent.firm}
          </div>
          <div className="ohf-row">
            <span className="ohf-lbl">Phone #:</span>{" "}
            {agentPhone && formatPhone(agentPhone)}
          </div>
          <div className="ohf-row">
            <span className="ohf-lbl">Email:</span> {agent.email}
          </div>
          <div className="ohf-row">
            <span className="ohf-lbl">License #:</span> {agent.license}
          </div>
          <div className="ohf-row ohf-price">
            <span className="ohf-lbl">Listing Price:</span>{" "}
            <span className="ohf-price-val">{money(prop.listed_price)}</span>
          </div>
        </div>
        <div className="ohf-photo">
          {homePhoto ? (
            <img src={homePhoto} alt="Home" className="ohf-photo-img" />
          ) : (
            <div className="ohf-photo-empty" />
          )}
        </div>
      </div>

      <div className="ohf-bar">FINANCING OPTIONS</div>
      <table className="ohf-table">
        <thead>
          <tr>
            <th className="ohf-th-terms">Loan and Payment Terms</th>
            {loans.map((l, i) => (
              <th key={i}>Loan {i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="ohf-rl">Down Payment %</td>
            {loans.map((l, i) => (
              <td key={i}>{pctTrim(l.percent_down, 2)}</td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Down Payment Amount</td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(actual_cash_down_payment(documentType, l, property))}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">
              Closing Cost Estimate
              <div className="ohf-sub">Includes all closing costs.</div>
            </td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(
                  total_closing_costs(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Estimated Cash To Close</td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(
                  estimated_cash_to_close_down_payment_with_closing_costs(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Total Cost to Purchase Home</td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(
                  total_cost_to_purchase(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Loan Amount</td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(
                  loan_amount_first_mortgage(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Loan Program</td>
            {loans.map((l, i) => (
              <td key={i}>{loan_product(l.term)}</td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Interest Rate</td>
            {loans.map((l, i) => (
              <td key={i}>{pctFixed(l.rate, 3)}</td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">APR</td>
            {loans.map((l, i) => (
              <td key={i}>
                {pctFixed(
                  apr(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  ),
                  3
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="ohf-rl">Payment Type</td>
            {loans.map((l, i) => (
              <td key={i}>{paymentType(l)}</td>
            ))}
          </tr>
          <tr className="ohf-pay">
            <td className="ohf-rl">
              Payment
              <div className="ohf-sub">
                Principal &amp; Interest, Taxes,
                <br />
                Home Insurance, HOA
              </div>
            </td>
            {loans.map((l, i) => (
              <td key={i}>
                {money(
                  total_monthly_payment(
                    documentType,
                    l,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <div className="ohf-eff">
        Rates are effective {effDate} and are subject to change without notice.
      </div>

      <div className="ohf-lo">
        <div className="ohf-logo">
          <Image url="company/company-logo" className="ohf-logo-img" />
        </div>
        <div className="ohf-lo-info">
          <div className="ohf-lo-name">
            {[lo.first_name, lo.last_name].filter(Boolean).join(" ")}
            {lo.title ? `  —  ${lo.title}` : ""}
          </div>
          <div>{co.company_name}</div>
          <div>
            {lo.office_phone ? `Office: ${formatPhone(lo.office_phone)}` : ""}
            {lo.cell_phone
              ? `${lo.office_phone ? "   or   " : ""}Cell: ${formatPhone(
                  lo.cell_phone
                )}`
              : ""}
          </div>
          <div>{lo.email ? `email: ${lo.email}` : ""}</div>
        </div>
      </div>
      <div className="ohf-disc">
        Call for your financing needs. ** Income qualifications are based on a
        borrower without a car payment, credit card payment, or student loan
        payments. Income needed will be adjusted according to other debt
        requirements. Please call for specific details and additional programs.
      </div>
      <div className="ohf-dre">
        {co.company_state_license
          ? `DRE Lic. # ${co.company_state_license}`
          : ""}
        {co.company_nmls_number
          ? `   -   Company NMLS Lic. #: ${co.company_nmls_number}`
          : ""}
        {lo.nmls_number ? `   -   Indv. # ${lo.nmls_number}` : ""}
      </div>
    </div>
  );
}
